import React from 'react';
import styled from 'styled-components';
import { FaHistory, FaGem, FaHandshake, FaUsers } from 'react-icons/fa';

// Components
import SectionTitle from '../components/SectionTitle';

const AboutPage = () => {
  return (
    <PageContainer>
      <HeroSection>
        <Container>
          <SectionTitle 
            title="About Master Collectors Forge" 
            subtitle="A passion for collecting, passed down through generations"
          />
        </Container>
      </HeroSection>
      
      <Section>
        <Container>
          <AboutContent>
            <AboutImage src="/images/about_us_image.png" alt="Master Collectors Forge - Family Tradition" />
            <AboutText>
              <h2>A Family Tradition</h2>
              <p>
                I can still picture the old leather coin album spread open on my grandfather's table as my father and I marveled at the coins inside. Beneath the warm glow of a lamp, my grandfather would carefully slide out a worn silver coin and share its story. To him, it wasn't just metal – it was a piece of history, and to me, it felt like magic. In those moments, I became the third generation of Miller coin collectors, soaking in not just the coins themselves but the stories and values they carried.
              </p>
              <p>
                Those early days of poring over coin albums with my family lit a spark in me that has never faded. Over twenty years later, I'm still that kid at heart whenever I discover a new treasure. In fact, that childhood wonder has only grown stronger with time. For me, collecting is more than a hobby—it's a way of life.
              </p>
            </AboutText>
          </AboutContent>
        </Container>
      </Section>
      
      <Section $alternate>
        <Container>
          <SectionTitle 
            title="Forging a Legacy" 
            subtitle="Turning passion into purpose"
          />
          
          <MissionStatement>
            That lifelong passion for collecting eventually inspired me to create <strong>Master Collectors Forge</strong>. I wanted to share the thrill I feel when holding a rare or historic coin, so I founded Master Collectors Forge as an online shop specializing in historical coins, collectible coins, and rounds. It began as my way of giving back to the hobby that shaped me. To this day, Master Collectors Forge is more than just a store—it's the culmination of a dream to turn my passion into a legacy I can share with others.
          </MissionStatement>
          
          <MissionStatement style={{ marginTop: '2rem' }}>
            Every coin or round that finds its way into my shop is <strong>carefully selected</strong> by me personally. I inspect each piece as if I were adding it to my own collection, ensuring it meets the highest standards of quality and authenticity. If an item isn't something I'd be proud to pass down or display myself, it doesn't make the cut. This commitment to quality is my way of honoring the craftsmanship and history behind each item, and it's a promise that every piece you find here represents the very best I have to offer.
          </MissionStatement>
        </Container>
      </Section>
      
      <Section>
        <Container>
          <SectionTitle 
            title="Creating Memories Together" 
            subtitle="Connecting generations through collecting"
          />
          
          <ValuesGrid>
            <ValueCard style={{ gridColumn: '1 / -1', maxWidth: '800px', margin: '0 auto' }}>
              <ValueDescription style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                Above all, I want Master Collectors Forge to be a place where you can create meaningful memories with your loved ones through collecting—just as I did with my own family. Whether you're a seasoned collector or a parent (or grandparent) introducing the next generation to this hobby, I'm here to help you find that special piece that becomes part of your story. Each coin and collectible in my shop has the potential to spark a connection or start a new tradition. I like to imagine that a coin you find here might one day be the centerpiece of a cherished memory, like the ones I share with my grandfather and father.
                <br /><br />
                Thank you for being a part of this journey with me. It's my honor as a fellow collector to help you <strong>forge new memories</strong> and carry on this timeless way of life. Happy collecting!
              </ValueDescription>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </Section>
      
      <Section $alternate>
        <Container>
          <SectionTitle 
            title="Our Expertise" 
            subtitle="Meet our team of experts"
          />
          
          <TeamGrid>
            <TeamMember>
              <TeamMemberImage src="/images/RyanMiller.jpg" alt="Ryan Miller" />
              <TeamMemberName>Ryan Miller</TeamMemberName>
              <TeamMemberRole>Founder & Collectible Expert</TeamMemberRole>
              <TeamMemberBio>
                With over 20 years of collecting experience, I've spent a lifetime chasing history, beauty, and value in every form it takes—whether it's rare coins, stunning gemstone specimens, or uniquely crafted silver rounds. My passion for collecting began as a personal journey and evolved into Master Collectors Forge, a trusted source for fellow enthusiasts and serious collectors alike.
              </TeamMemberBio>
            </TeamMember>
            
            <TeamMember>
              <TeamMemberImage src="/images/MarcMiller.jpg" alt="Marc Miller" />
              <TeamMemberName>Marc Miller</TeamMemberName>
              <TeamMemberRole>Collectibles Specialist</TeamMemberRole>
              <TeamMemberBio>
                Marc's expertise in collectibles and numismatics has made him a trusted advisor for collectors and enthusiasts alike.
              </TeamMemberBio>
            </TeamMember>
            
            <TeamMember>
              <TeamMemberImage src="/images/team-member-3.jpg" alt="Kent Miller" />
              <TeamMemberName>Kent Miller</TeamMemberName>
              <TeamMemberRole>Authentication Director</TeamMemberRole>
              <TeamMemberBio>
                Kent leads our authentication process, ensuring that every item in our collection meets our rigorous standards for authenticity.
              </TeamMemberBio>
            </TeamMember>
          </TeamGrid>
        </Container>
      </Section>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div``;

const HeroSection = styled.section`
  background: ${({ theme }) => theme.gradients.darkBlue};
  padding: 3rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.metallic.gold};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Section = styled.section`
  padding: 5rem 0;
  background-color: ${({ $alternate, theme }) => 
    $alternate ? theme.colors.background.light : '#fff'};
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: 2rem;
  }
`;

const AboutText = styled.div`
  h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.colors.text.dark};
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
    color: ${({ theme }) => theme.colors.text.dark};
  }
`;

const MissionStatement = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text.dark};
  font-style: italic;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.25rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ValueCard = styled.div`
  background-color: #fff;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
  transition: transform ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const TeamMember = styled.div`
  background-color: #fff;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: transform ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const TeamMemberImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

const TeamMemberName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.5rem 1.5rem 0.5rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const TeamMemberRole = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 1.5rem 1rem;
`;

const TeamMemberBio = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0 1.5rem 1.5rem;
`;

export default AboutPage;
